'use client';

import { useState, useEffect } from 'react';
import { useTheme } from '@/components/ThemeContext';
import Link from 'next/link';
import {
    Terminal, Network, Brain, BarChart3, ClipboardList, Radio,
    CalendarClock, FileText, Pencil, Zap, Sparkles, Monitor,
    Database, Settings2, MessageCircle, Shield, Search, Bot,
    FolderSearch, Coins, Wallet, Lock, Eye, Layers, Activity,
    HardDrive, Globe, Users, Server, Cloud, Building2, Award,
    TrendingUp, BookOpen, ChevronRight, ChevronDown, ExternalLink,
    type LucideIcon,
} from 'lucide-react';

/* ═══════════════════════════════════════════════
   SnapCodex 전체 기능 활용 매뉴얼 (Guide)
   41개 페이지 · 95개 API · 9-Node Grid
   ═══════════════════════════════════════════════ */

// ── 가이드 섹션 정의 ──
type Section = 'overview' | 'core' | 'ai-agents' | 'infrastructure' | 'admin' | 'api' | 'quickstart';

interface GuideItem {
  title: string;
  path: string;
  icon: LucideIcon;
  color: string;
  desc: string;
  howTo?: string[];
  tips?: string[];
}

// ── 플랫폼 개요 ──
const PLATFORM_STATS = [
  { label: '웹 페이지', value: '34', icon: Globe, color: '#6366f1' },
  { label: 'API 라우트', value: '126', icon: Server, color: '#8b5cf6' },
  { label: 'AI 에이전트', value: '10+', icon: Bot, color: '#06b6d4' },
  { label: '그리드 노드', value: '9', icon: Network, color: '#10b981' },
  { label: 'NAS 파일', value: '47K+', icon: HardDrive, color: '#f59e0b' },
  { label: 'GraphRAG 캐시', value: 'Neo4j', icon: Database, color: '#ef4444' },
];

// ── 핵심 기능 ──
const CORE_FEATURES: GuideItem[] = [
  {
    title: 'SnapAgent LIVE', path: '/live', icon: Radio, color: '#22c55e',
    desc: '9개 노드의 실시간 상태, AI 검색, 견적 생성, NAS 파일 탐색을 단일 대시보드에서 수행합니다.',
    howTo: [
      '접속 → 비밀번호 입력 (관리자 발급)',
      'Stream 탭: 실시간 노드 상태 + Company Overview KPI 확인',
      'Search 탭: 자연어로 NAS 프로젝트 검색 (예: "A프로젝트 2층 평면도")',
      'Agents 탭: 작업 입력 → 9개 노드에 자동 분배 → 결과 실시간 확인',
      'Price 탭: 프로젝트 정보 입력 → AI 견적서 자동 생성',
      'RAG Explorer 탭: NAS 파일 탐색 + 미리보기 + AI 챗봇 질의',
    ],
    tips: ['검색 결과에 "유용해요/아쉬워요" 피드백을 남기면 AI가 학습합니다', 'RAG Explorer에서 파일 드래그앤드롭으로 업로드 가능'],
  },
  {
    title: 'AI RAG 챗봇', path: '/chat', icon: MessageCircle, color: '#818cf8',
    desc: 'NAS에 저장된 3TB+ 설계 문서를 기반으로 자연어 질문에 전문가 답변을 제공합니다.',
    howTo: [
      '/live → RAG Explorer → 파일 선택 → Chat 탭',
      '파일을 선택한 상태에서 질문하면 해당 문서 맥락으로 답변',
      '파일 미선택 시 전체 캐시된 문서에서 답변 탐색',
      '텔레그램(@desinsight_Poby_bot)으로도 동일 질의 가능',
    ],
    tips: ['먼저 파일을 "사전 캐시"(precache)해야 정확한 답변이 나옵니다', '답변에 "[🚀 Neo4j Fast GraphRAG]" 태그가 뜨면 새 분석, "[⚡ Fast Cache RAG]"는 캐시 히트'],
  },
  {
    title: 'NAS 파일 탐색 & 사전 캐시', path: '/live', icon: FolderSearch, color: '#f97316',
    desc: 'NAS의 203개 프로젝트 폴더를 웹에서 탐색하고, 파일 클릭 시 자동으로 AI가 텍스트를 추출·분석합니다.',
    howTo: [
      '/live → RAG Explorer 탭 접속 (승인 키 필요)',
      '좌측 트리에서 폴더 탐색 → 파일 클릭',
      '자동으로 precache 실행: 텍스트 추출 → Gemini Vision 분석 → Neo4j 저장',
      '미리보기: 이미지/PDF/CAD/Office/HWP 등 11개 형식 지원',
      '파일 다운로드 버튼으로 원본 다운로드',
    ],
    tips: ['이미지/투시도는 Gemini Vision이 자동 분석하여 공간 정보를 추출합니다', 'DWG 파일은 레이어·텍스트 정보가 자동 추출됩니다'],
  },
  {
    title: '공정 관리 (Scheduler)', path: '/scheduler', icon: CalendarClock, color: '#0ea5e9',
    desc: '6주 단위 공정표를 시각적으로 관리합니다. Flow.team 데이터 자동 마이그레이션 지원.',
    howTo: [
      '/scheduler 접속 → 프로젝트 선택 또는 새 프로젝트 생성',
      '태스크 추가: 이름, 담당자, 시작일, 종료일 지정',
      '간트차트에서 드래그로 일정 조정',
      '진행률 업데이트: 태스크 클릭 → % 조정',
    ],
  },
  {
    title: 'AI 검색 (40분→0.3초)', path: '/live', icon: Search, color: '#a855f7',
    desc: 'NAS 3TB+ 파일을 자연어로 즉시 검색합니다. 기존 수동 검색 대비 8000배 속도 개선.',
    howTo: [
      '/live → Search 탭',
      '검색창에 자연어 입력 (예: "A프로젝트 투시도", "2024년 상업 인테리어")',
      '파일 유형 필터: 전체/DWG/PDF/Excel/회의록',
      '결과에서 프로젝트 클릭 → 상세 파일 목록',
      '유용성 피드백으로 검색 품질 개선',
    ],
  },
  {
    title: 'DSLM 인사이트 리포트', path: '/live', icon: Sparkles, color: '#f43f5e',
    desc: 'NAS 변경 이력, Flow 업무 맥락, 교차 프로젝트 단가를 통합 분석하여 AI PM 리포트를 생성합니다.',
    howTo: [
      '/live → DSLM 인사이트 탭 접속',
      '분석할 프로젝트 ID 입력 (예: ID_2531)',
      '"리포트 생성" 클릭 → Gemma-4 Brain이 실시간 추론 시작',
      '결과 확인: 진행 단계 판정, 핵심 리스크(누락 과업), 단가 적정성, PM 권고 사항',
    ],
    tips: ['실시간 NAS 변경사항과 업무연락(Flow)이 결합되어 가장 정확한 현장 파악이 가능합니다', '교차 단가 분석을 통해 타 현장 대비 공사비 적정성을 즉시 확인하세요'],
  },
];

// ── AI 에이전트 ──
const AI_AGENTS: GuideItem[] = [
  {
    title: 'BOQ Agent (견적)', path: '/boq-agent', icon: ClipboardList, color: '#f59e0b',
    desc: 'AI 기반 자동 견적서 생성. 프로젝트 정보 입력 → 항목별 단가·수량·금액 산출.',
    howTo: ['프로젝트명·면적·용도·등급 입력', '"AI 견적 생성" 클릭 → 항목별 상세 견적서 출력', '벤치마크 비교·신뢰도 확인 가능'],
  },
  {
    title: 'Cost Agent (원가)', path: '/cost-agent', icon: Coins, color: '#ef4444',
    desc: '공사비 최적화 분석. 내역서를 업로드하면 AI가 단가 적정성을 검토합니다.',
    howTo: ['내역서 파일 업로드 또는 NAS 경로 지정', 'AI 분석 → 단가 비교·과다 계상 항목 경고', 'VE(Value Engineering) 제안 자동 생성'],
  },
  {
    title: 'CAD Agent (도면)', path: '/cad-agent', icon: FileText, color: '#3b82f6',
    desc: 'DWG/DXF 도면 파싱. 레이어·객체·치수를 추출하고 버전 간 변경사항을 비교합니다.',
    howTo: ['NAS에서 도면 파일 선택', 'AI 분석 → 레이어 목록·면적·텍스트 추출', '두 버전 비교: 추가/삭제/변경된 객체 하이라이트'],
  },
  {
    title: 'Design Agent (설계 검토)', path: '/design-agent', icon: Layers, color: '#8b5cf6',
    desc: 'NAS 프로젝트 자동 감지. 설계 문서를 크로스체킹하여 불일치 항목을 경고합니다.',
    howTo: ['NAS 프로젝트 자동 감지 → 선택', 'AI가 도면↔내역서↔시방서 관계를 교차 검증', '경고 항목: 누락·초과 계상·규격 불일치'],
  },
  {
    title: 'BTL Agent (공공건축)', path: '/btl-agent', icon: Building2, color: '#059669',
    desc: 'BTL 입찰 문서 분석 특화. 발주처 요구사항과 설계안을 자동 대조합니다.',
    howTo: ['입찰안내서 업로드', 'AI가 요구사항 항목별 분석·충족 여부 체크', '불일치·모호한 항목 자동 식별 → 질의서 초안 생성'],
  },
  {
    title: 'Furniture Agent', path: '/furniture-agent', icon: BarChart3, color: '#d946ef',
    desc: '가구·집기 견적 자동화. 수동 입력 또는 NAS 도면 분석으로 견적서를 생성합니다.',
    howTo: ['품목 수동 추가: 품명·규격·수량·단가 입력', '또는 NAS 도면 경로 지정 → AI 자동 추출', '"견적서 생성" → 합계·세부 항목 엑셀 출력'],
  },
  {
    title: 'Rendering Agent', path: '/ren-agent', icon: Eye, color: '#ec4899',
    desc: 'NAS의 투시도·렌더링 이미지를 AI가 분석하여 공간 정보와 마감재를 자동 추출합니다.',
    howTo: ['NAS 탐색 → 이미지 파일 선택 (JPG/PNG/PSD)', 'Gemini Vision이 공간·재질·조명 분석', '분석 결과를 다른 에이전트에 컨텍스트로 전달'],
  },
];

// ── 인프라 & 관리 ──
const INFRA_ITEMS: GuideItem[] = [
  {
    title: 'Ontology Viewer', path: '/ontology', icon: Brain, color: '#6366f1',
    desc: 'Neo4j 기반 온톨로지(지식 그래프) 시각화. NAS 프로젝트 구조를 관계형 그래프로 탐색합니다.',
    howTo: ['전체 동기화: NAS → Neo4j 프로젝트 구조 동기화', 'CYPHER 쿼리 직접 실행 가능', 'NAS 경로 입력 → 폴더 구조 온톨로지 생성'],
  },
  {
    title: 'Provisioning', path: '/admin/provision', icon: Zap, color: '#f59e0b',
    desc: '원클릭 고객사 환경 구축. NAS 폴더 생성 → Tailscale 연결 → Docker 배포 → DB 초기화까지 9단계 자동화.',
    howTo: ['회사명·요금제(Starter/Pro/Elite) 선택', '"원클릭 프로비저닝 실행" → 9단계 자동 진행', '완료 후 테넌트 목록에 자동 등록'],
  },
  {
    title: 'Cache Dashboard', path: '/admin/cache', icon: Database, color: '#10b981',
    desc: 'Neo4j 캐시 현황 모니터링. FAQ 사전 캐싱, 배치 캐싱 실행, 캐시 통계 확인.',
    howTo: ['캐시 현황: DocumentContext·SemanticCache 통계', 'FAQ 사전 캐싱: 질문 목록 입력 → 일괄 캐싱', '배치 캐싱: NAS 프로젝트 단위 전체 캐싱'],
  },
  {
    title: 'NAS 아키텍처', path: '/admin/nas-diagram', icon: HardDrive, color: '#8b5cf6',
    desc: 'Poby NAS(기존)와 SnapCodex NAS(전용)의 역할·데이터 흐름을 3D 애니메이션으로 시각화.',
    howTo: ['노드 클릭 → 상세 기능·폴더 구조 확인', '역할 비교 테이블: 용도·보안·AI 연동 차이점', '데이터 흐름 시퀀스: RAG 파이프라인 4단계 확인'],
  },
  {
    title: 'IR (Investor Relations)', path: '/ir', icon: TrendingUp, color: '#3b82f6',
    desc: 'SnapCodex 기술 가치와 투자 매력도를 시각적으로 보여주는 IR 페이지.',
    howTo: ['Data Moat 지표 확인: API 수·기능 수·노드 수', '실증 프로젝트 성과 확인'],
  },
  {
    title: '실증 사례 (Case Study)', path: '/case/ptk', icon: Award, color: '#059669',
    desc: 'A프로젝트 실증 완료 케이스 스터디. Before→After 4대 성과 패키징.',
    howTo: ['프로젝트 개요: 규모·면적·공간 구성 확인', 'Before→After: 수동 vs AI 성과 비교', '기술 스택: 적용된 AI·인프라 확인'],
  },
];

// ── 빠른 시작 가이드 ──
const QUICKSTART_STEPS = [
  { step: 1, title: '로그인', desc: 'desinsight.com 접속 → 상단 네비게이션에서 원하는 기능 선택', icon: Globe },
  { step: 2, title: 'LIVE 대시보드', desc: '/live 접속 → 비밀번호 입력 → 실시간 그리드 상태 확인', icon: Radio },
  { step: 3, title: 'NAS 파일 탐색', desc: 'RAG Explorer 탭 → 승인 키 입력 → 폴더 탐색 → 파일 클릭', icon: FolderSearch },
  { step: 4, title: '사전 캐시', desc: '파일 클릭 시 자동 실행: 텍스트 추출 → Gemini 분석 → Neo4j 저장', icon: Database },
  { step: 5, title: 'AI 질의', desc: 'Chat 탭에서 파일에 대해 자연어 질문 → AI 전문가 답변', icon: MessageCircle },
  { step: 6, title: 'DSLM 인사이트', desc: 'DSLM 인사이트 탭 → 프로젝트 통합 분석 리포트 생성 (PM 지능)', icon: Sparkles },
  { step: 7, title: 'AI 견적', desc: 'Price 탭 → 프로젝트 정보 입력 → AI 견적서 자동 생성', icon: Coins },
];

// ── 연동 채널 ──
const CHANNELS = [
  { name: 'Web (desinsight.com)', desc: '41개 페이지, 모든 기능', icon: Globe, color: '#6366f1' },
  { name: 'Telegram Bot', desc: '@desinsight_Poby_bot — RAG 질의·파일 검색', icon: MessageCircle, color: '#0088cc' },
  { name: 'KakaoTalk', desc: 'i Open Builder 챗봇 — 자연어 질의', icon: MessageCircle, color: '#fee500' },
];

function SectionCard({ item, isDark }: { item: GuideItem; isDark: boolean }) {
  const [open, setOpen] = useState(false);
  const Icon = item.icon;

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all ${isDark ? 'bg-gray-900/80 border-gray-800 hover:border-gray-700' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'}`}>
      <div className="p-5 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}15` }}>
            <Icon className="w-5 h-5" style={{ color: item.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link href={item.path} onClick={e => e.stopPropagation()} className="text-[10px] px-2 py-0.5 rounded-full font-mono" style={{ background: `${item.color}15`, color: item.color }}>
                  {item.path} <ExternalLink className="w-2.5 h-2.5 inline-block" />
                </Link>
                {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
              </div>
            </div>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.desc}</p>
          </div>
        </div>
      </div>
      {open && (item.howTo || item.tips) && (
        <div className={`px-5 pb-5 space-y-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          {item.howTo && (
            <div className="pt-4">
              <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>사용법</h4>
              <ol className="space-y-1.5">
                {item.howTo.map((step, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: `${item.color}15`, color: item.color }}>{i + 1}</span>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {item.tips && (
            <div>
              <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-yellow-500/70' : 'text-yellow-600'}`}>Tips</h4>
              {item.tips.map((tip, i) => (
                <div key={i} className={`text-sm flex gap-2 ${isDark ? 'text-yellow-400/70' : 'text-yellow-700'}`}>
                  <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{tip}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function GuidePage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [section, setSection] = useState<Section>('overview');

  const bg = isDark ? 'bg-gray-950' : 'bg-gray-50';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-600';

  const tabs: { id: Section; label: string; icon: LucideIcon }[] = [
    { id: 'overview', label: '개요', icon: BookOpen },
    { id: 'quickstart', label: '빠른 시작', icon: Zap },
    { id: 'core', label: '핵심 기능', icon: Radio },
    { id: 'ai-agents', label: 'AI 에이전트', icon: Bot },
    { id: 'infrastructure', label: '인프라 & 관리', icon: Server },
  ];

  return (
    <div className={`min-h-screen pt-16 pb-20 transition-colors ${bg}`}>
      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 pt-8 pb-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 mb-4">
            <BookOpen className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-sm font-medium bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">SnapCodex 활용 매뉴얼</span>
          </div>
          <h1 className={`text-3xl md:text-4xl font-black mb-3 tracking-tight ${textPrimary}`}>
            SnapCodex <span className="bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">User Guide</span>
          </h1>
          <p className={`text-lg ${textSecondary}`}>전체 기능 활용법 · 41개 페이지 · 95개 API · 10+ AI 에이전트</p>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-6xl mx-auto px-6 mb-8">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {PLATFORM_STATS.map(s => {
            const Icon = s.icon;
            return (
              <div key={s.label} className={`rounded-xl p-3 border text-center ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-white border-gray-200 shadow-sm'}`}>
                <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: s.color }} />
                <div className="text-xl font-black" style={{ color: s.color }}>{s.value}</div>
                <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-6 mb-8">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setSection(tab.id)}
                className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${section === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25'
                  : `${isDark ? 'bg-gray-900 text-gray-400 hover:text-white' : 'bg-white text-gray-500 hover:text-gray-900'} border ${isDark ? 'border-gray-800' : 'border-gray-200'}`}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6">
        {/* ── 개요 ── */}
        {section === 'overview' && (
          <div className="space-y-6">
            <div className={`rounded-2xl p-6 border ${isDark ? 'bg-gray-900/80 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>
              <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>SnapCodex란?</h2>
              <p className={`text-sm leading-relaxed ${textSecondary}`}>
                SnapCodex는 <strong>건축 프로젝트의 설계·시공·견적 데이터를 AI로 통합 분석</strong>하는 플랫폼입니다.
                NAS에 저장된 수만 개의 CAD 도면, PDF 내역서, 이미지 투시도를 자동으로 텍스트 추출·벡터화하고,
                Gemini 2.5 Flash 기반 RAG(Retrieval-Augmented Generation) 파이프라인을 통해
                자연어로 질문하고 전문가 수준의 답변을 받을 수 있습니다.
              </p>
              <div className="mt-4 grid md:grid-cols-3 gap-4">
                {[
                  { title: '데이터 통합', desc: 'NAS 3TB+ 파일을 자동 인덱싱하여 관계형 지식 그래프 구축', icon: Database },
                  { title: 'AI 크로스체킹', desc: '도면↔내역서↔시방서 간 불일치를 AI가 자동 감지·경고', icon: Shield },
                  { title: '온프레미스 지원', desc: '100% 폐쇄망 환경 지원 — 고객 데이터 완전 자율 통제', icon: Lock },
                ].map(f => {
                  const Icon = f.icon;
                  return (
                    <div key={f.title} className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-gray-50 border-gray-100'}`}>
                      <Icon className={`w-5 h-5 mb-2 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                      <h4 className={`font-bold text-sm mb-1 ${textPrimary}`}>{f.title}</h4>
                      <p className={`text-xs ${textSecondary}`}>{f.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 연동 채널 */}
            <div className={`rounded-2xl p-6 border ${isDark ? 'bg-gray-900/80 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>
              <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>연동 채널</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {CHANNELS.map(ch => {
                  const Icon = ch.icon;
                  return (
                    <div key={ch.name} className={`flex items-center gap-3 p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-gray-50 border-gray-100'}`}>
                      <Icon className="w-5 h-5" style={{ color: ch.color }} />
                      <div>
                        <div className={`font-bold text-sm ${textPrimary}`}>{ch.name}</div>
                        <div className={`text-xs ${textSecondary}`}>{ch.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── 빠른 시작 ── */}
        {section === 'quickstart' && (
          <div className={`rounded-2xl p-6 border ${isDark ? 'bg-gray-900/80 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>
            <h2 className={`text-xl font-bold mb-6 ${textPrimary}`}>빠른 시작 가이드 (6단계)</h2>
            <div className="space-y-0">
              {QUICKSTART_STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.step} className="flex items-stretch">
                    <div className="flex flex-col items-center mr-5">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-lg">{s.step}</div>
                      {i < QUICKSTART_STEPS.length - 1 && <div className={`w-0.5 flex-1 my-1 ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`} />}
                    </div>
                    <div className={`pb-8 ${i === QUICKSTART_STEPS.length - 1 ? 'pb-0' : ''}`}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-indigo-400" />
                        <h3 className={`font-bold ${textPrimary}`}>{s.title}</h3>
                      </div>
                      <p className={`text-sm mt-1 ${textSecondary}`}>{s.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── 핵심 기능 ── */}
        {section === 'core' && (
          <div className="space-y-4">
            {CORE_FEATURES.map(item => (
              <SectionCard key={item.path + item.title} item={item} isDark={isDark} />
            ))}
          </div>
        )}

        {/* ── AI 에이전트 ── */}
        {section === 'ai-agents' && (
          <div className="space-y-4">
            <div className={`rounded-xl p-4 border mb-4 ${isDark ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'}`}>
              <p className={`text-sm ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                <Bot className="w-4 h-4 inline-block mr-1" />
                모든 AI 에이전트는 <strong>Gemini 2.5 Flash</strong>를 기반으로 동작하며, NAS 문서 컨텍스트와 결합하여 건축 전문가 수준의 분석을 제공합니다.
              </p>
            </div>
            {AI_AGENTS.map(item => (
              <SectionCard key={item.path + item.title} item={item} isDark={isDark} />
            ))}
          </div>
        )}

        {/* ── 인프라 & 관리 ── */}
        {section === 'infrastructure' && (
          <div className="space-y-4">
            {INFRA_ITEMS.map(item => (
              <SectionCard key={item.path + item.title} item={item} isDark={isDark} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-6xl mx-auto px-6 mt-16">
        <div className={`text-center text-sm ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
          <p>DesignInsight Inc. © 2026 — SnapCodex Platform Manual v2.0</p>
          <p className="mt-1">34 Pages · 126 APIs · 9 Grid Nodes · Gemini 2.5 Flash · Neo4j AuraDB</p>
        </div>
      </div>
    </div>
  );
}
